import { DeleteOutlined } from '@ant-design/icons';
import { Button, Flex, Form, FormInstance, Popconfirm, PopconfirmProps, Spin } from 'antd';
import dayjs from 'dayjs';
import React, { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useMutation, useRelayEnvironment } from 'react-relay';

import AccessContainer from '../../components/AccessContainer';
import FieldInputTextAreaBuffer from '../../components/fieldInputTextAreaBuffer';
import FieldStatusGrouping from '../../components/FieldStatus/fieldStatusGrouping';
import WrapperTwoColumn from '../../components/WrapperTwoColumn';
import { COLOR_GREY_E9, COLOR_ITEM, FORMAT_DATE_FOR_SAVE, FORMAT_TIME } from '../../const';
import { checkAssigneeData, findPhaseScheduleItem, getObjectFromRelayStoreById } from '../../functions';
import FieldPriority from '../../gant/formField/fieldPriority';
import { TaskTypeInput } from '../../graphql/__generated__/UpdateTaskMutation.graphql';
import DeleteTaskMutation from '../../graphql/mutations/DeleteTaskMutation';
import UpdateTaskMutation from '../../graphql/mutations/UpdateTaskMutation';
import useAccessForTask from '../../hooks/useAccessForTask';
import useCommitTask from '../../hooks/useCommitTask';
import { ComponentsDispatch, RootState } from '../../store';
import { setterCalendar } from '../../store/slices/calendarSlice';
import { setterCalendarTasks } from '../../store/slices/calendarTasksSlice';
import { addSpinnerItem, removeSpinnerItem } from '../../store/slices/spinnerSlice';
import {
    ItemTaskType,
    ListItemType,
    MappingItemIdIndexType,
    PermissionCombinationType,
    ScheduleItemType,
} from '../../types';
import FieldDate from '../formField/fieldDate';
import FieldEndTime from '../formField/fieldEndTime';
import FieldNumber from '../formField/fieldNumber';
import FieldStartTime from '../formField/fieldStartTime';
import FieldStatus from '../formField/fieldStatus';
import FieldType from '../formField/fieldType';
import TabsItems from '../formField/tabsItems';
import TabsResource from '../formField/tabsResource';
import useTypeInitializer from '../hooks/useTypeInitializer';

const DELAY_UPDATING_TASK = 500;

interface ICalendarTaskForm {
    form: FormInstance<ItemTaskType>;
}

export const CalendarTaskForm: React.FC<ICalendarTaskForm> = ({ form }) => {
    const { isFirstEditing } = useTypeInitializer(form);
    const environment = useRelayEnvironment();
    const dispatch = useDispatch<ComponentsDispatch>();
    const [commitUpdate, isInFlightUpdate] = useMutation(UpdateTaskMutation);
    const [commitDelete, isInFlightDelete] = useMutation(DeleteTaskMutation);
    const { getCommitTask, getCommitTaskDelete } = useCommitTask('day');

    const currentTask = useSelector<RootState, ItemTaskType | null>((state) => state.calendar.currentTask);
    const connectionTaskId = useSelector<RootState, string>((state) => state.calendar.connectionTaskId);
    const spinner = useSelector<RootState, boolean>((state) => state.spinner.spinner);
    const mappingItemIdIndex = useSelector<RootState, MappingItemIdIndexType>(
        (state) => state.calendar.mappingItemIdIndex,
    );

    const isAccessStatus = useAccessForTask(currentTask, [
        'schedule-task.all.edit',
        'schedule-task.all.manage',
        'schedule-task.owner.edit',
        'schedule-task.owner.manage',
        'schedule-task.assignee.edit',
        'schedule-task.assignee.manage',
    ]);

    const accessForEditTask: PermissionCombinationType[] = [
        'schedule-task.all.edit',
        'schedule-task.all.manage',
        'schedule-task.owner.edit',
        'schedule-task.owner.manage',
        'schedule-task.assignee.edit',
        'schedule-task.assignee.manage',
    ];

    if (isFirstEditing) {
        accessForEditTask.concat([
            'schedule-task.all.create',
            'schedule-task.assignee.edit',
            'schedule-task.owner.create',
        ]);
    }

    const isAccessToEditTask = useAccessForTask(currentTask, [
        ...accessForEditTask,
        ...(isFirstEditing
            ? ([
                  'schedule-task.all.create',
                  'schedule-task.assignee.edit',
                  'schedule-task.owner.create',
              ] as PermissionCombinationType[])
            : []),
    ]);

    const values = Form.useWatch([], { form, preserve: true });

    const timeoutRef = useRef<NodeJS.Timeout | null>(null);

    useEffect(() => {
        if (currentTask) {
            form.setFieldsValue({
                ...currentTask,
                start_datetime: dayjs(currentTask.start_datetime, 'HH:mm') as any,
                end_datetime: dayjs(currentTask.end_datetime, 'HH:mm') as any,
                datetime: dayjs(currentTask.datetime) as any,
                assignee_for_task: checkAssigneeData(currentTask.assignee_for_task),
                color: currentTask.color || COLOR_ITEM,
                status: currentTask.status?.toString() as any,
                sub_status: currentTask.sub_status?.toString() as any,
                type: currentTask.type?.toString() as any,
                priority: currentTask.priority?.toString() as any,
                phase_id: currentTask.phase_id?.toString() as any,
                division_id: currentTask.division_id?.toString() as any,
                cost_code_id: currentTask.cost_code_id?.toString() as any,
            });
        }
    }, [currentTask]);

    useEffect(() => {
        if (isInFlightDelete) {
            dispatch(addSpinnerItem('deleteTask'));
        } else {
            dispatch(removeSpinnerItem('deleteTask'));
        }
    }, [isInFlightDelete]);

    const normalizeNumberValue = (value?: number) => {
        return value ? +value : null;
    };

    useEffect(() => {
        if (!values || !currentTask) {
            return;
        }

        dispatch(
            setterCalendarTasks({
                wasChangedTask: true,
            }),
        );

        const currentValues = { ...values };

        // field name is dynamic field and locate to another form but that use this controller
        // and if user doesn't edit name than 'values' doesn't include data from field name
        // to avoid error add value manually to 'values'
        if (!currentValues.name) {
            currentValues.name = currentTask?.name || '';
        }

        if (timeoutRef?.current) {
            clearTimeout(timeoutRef.current);
        }

        timeoutRef.current = setTimeout(() => {
            dispatch(
                setterCalendarTasks({
                    wasChangedTask: false,
                }),
            );

            if (!currentValues?.datetime) {
                return;
            }

            const currentDate = (values?.datetime as any).format(FORMAT_DATE_FOR_SAVE);

            const newTaskForm: ItemTaskType = {
                ...currentValues,
                start_datetime: (currentValues?.start_datetime as any).format(FORMAT_TIME),
                end_datetime: (currentValues?.end_datetime as any).format(FORMAT_TIME),
                schedule_item_id: currentValues.schedule_item_id || null,
                assignee_for_task: checkAssigneeData(currentValues.assignee_for_task),
                project_id: currentValues.project_id || null,
                description: currentValues.description || null,
                value_equipment: currentValues.value_equipment || null,
                value_labour: currentValues.value_labour || null,
                value_material: currentValues.value_material || null,
                value_other: currentValues.value_other || null,
                value_total: currentValues.value_total || null,
                owner_task_id: currentValues.owner_task_id || null,
                datetime: currentDate,
                status: normalizeNumberValue(currentValues.status!) as any,
                sub_status: normalizeNumberValue(currentValues.sub_status!) as any,
                type: normalizeNumberValue(currentValues.type!) as any,
                priority: normalizeNumberValue(currentValues.priority!) as any,
                phase_id: normalizeNumberValue(currentValues.phase_id!) as any,
                division_id: normalizeNumberValue(currentValues.division_id!) as any,
                cost_code_id: normalizeNumberValue(currentValues.cost_code_id!) as any,
                id: currentTask?.id!,
            };

            const newTask = {
                ...currentTask,
                ...newTaskForm,
            };

            if (newTask.schedule_item_id) {
                const phaseId = findPhaseScheduleItem(newTask.schedule_item_id, environment)?.toString();
                form.setFieldsValue({
                    phase_id: phaseId as any,
                });
            }

            if (currentTask.project_id !== newTask.project_id) {
                newTask.phase_id = null;
                newTask.schedule_item_id = null;
            }

            if (JSON.stringify(currentTask).replace(' 00:00:00', '') !== JSON.stringify(newTask)) {
                dispatch(
                    setterCalendar({
                        currentTask: newTask,
                    }),
                );

                commitUpdate(
                    getCommitTask(
                        {
                            ...newTask,
                            assignee_for_task: checkAssigneeData(newTask.assignee_for_task).join(','),
                        } as TaskTypeInput,
                        connectionTaskId,
                        currentTask?.id,
                    ),
                );
            }
        }, DELAY_UPDATING_TASK);

        return () => {
            if (timeoutRef?.current) {
                clearTimeout(timeoutRef.current);
            }
        };
    }, [values]);

    useEffect(() => {
        let newStatus = null;

        if (values?.sub_status) {
            const statusListItem = getObjectFromRelayStoreById<ListItemType>(
                environment,
                values?.sub_status.toString(),
            );

            if (statusListItem?.parent) {
                newStatus = statusListItem?.parent;
            }
        }

        form.setFieldsValue({
            status: newStatus as any,
        });
    }, [values?.sub_status]);

    useEffect(() => {
        const item = getObjectFromRelayStoreById<ScheduleItemType>(environment, values?.schedule_item_id || '');

        form.setFieldsValue({
            color: values?.schedule_item_id && item?.color ? item?.color : COLOR_ITEM,
        });
    }, [values?.schedule_item_id]);

    const onClickDelete: PopconfirmProps['onConfirm'] = (e?: React.MouseEvent<HTMLElement, MouseEvent>) => {
        e?.stopPropagation();

        if (currentTask?.id) {
            const resetCurrentTask = () => {
                dispatch(
                    setterCalendar({
                        currentTask: null,
                    }),
                );
            };

            commitDelete(getCommitTaskDelete(currentTask?.id!, connectionTaskId, resetCurrentTask));
        }
    };

    return (
        <Flex vertical flex={1} style={{ width: '100%', height: '100%' }}>
            <div
                style={{
                    flex: 1,
                    overflowY: 'auto',
                    width: '100%',
                }}
            >
                <Spin tip={'Saving...'} spinning={spinner} style={{ width: '100%' }}>
                    <Flex vertical style={{ padding: '16px 24px 0' }}>
                        <Form
                            name="taskItemForm"
                            layout="vertical"
                            form={form}
                            initialValues={{ remember: true }}
                            autoComplete="off"
                        >
                            <WrapperTwoColumn
                                childFirstColumn={
                                    <>
                                        <FieldType disabled={!isAccessToEditTask} />
                                        <FieldStatus disabled={!isAccessStatus} />
                                        <FieldStatusGrouping
                                            name={'sub_status'}
                                            groupStatus={true}
                                            disabled={!isAccessStatus}
                                        />
                                    </>
                                }
                                childSecondColumn={
                                    <>
                                        <FieldPriority disabled={!isAccessToEditTask} />
                                        <FieldNumber />
                                    </>
                                }
                                style={{}}
                            />
                            <WrapperTwoColumn
                                childFirstColumn={
                                    <>
                                        <FieldDate disabled={!isAccessToEditTask} />
                                    </>
                                }
                                childSecondColumn={
                                    <>
                                        <Flex justify={'space-between'}>
                                            <FieldStartTime disabled={!isAccessToEditTask} />
                                            <FieldEndTime disabled={!isAccessToEditTask} />
                                        </Flex>
                                    </>
                                }
                                style={{}}
                            />
                            <FieldInputTextAreaBuffer
                                disabled={!isAccessToEditTask}
                                nameField={'description'}
                                labelField={'Description'}
                            />
                        </Form>
                        <TabsItems form={form} isInFlightUpdate={isInFlightUpdate} />
                        <Form
                            name="taskItemForm2"
                            layout="vertical"
                            form={form}
                            initialValues={{ remember: true }}
                            autoComplete="off"
                        >
                            <TabsResource />
                        </Form>
                    </Flex>
                </Spin>
            </div>
            <AccessContainer
                accessFor={['schedule-task.all.manage', 'schedule-task.owner.manage', 'schedule-task.assignee.manage']}
            >
                <Flex
                    justify={'center'}
                    style={{
                        padding: '16px 24px',
                        borderTop: `1px solid ${COLOR_GREY_E9}`,
                    }}
                >
                    <Spin spinning={spinner} size={'small'} style={{ width: '100%' }}>
                        <Flex justify={'center'} gap={20}>
                            <Popconfirm
                                title="Delete the task"
                                description="Are you sure to delete this task?"
                                onConfirm={onClickDelete}
                                okText="Yes"
                                cancelText="No"
                            >
                                <Button
                                    type="default"
                                    danger
                                    htmlType="submit"
                                    icon={<DeleteOutlined />}
                                    style={{ width: 100, alignSelf: 'end' }}
                                >
                                    Delete
                                </Button>
                            </Popconfirm>
                        </Flex>
                    </Spin>
                </Flex>
            </AccessContainer>
        </Flex>
    );
};
